/**
 * Anel "Por canal": de onde veio o valor recuperado (ligacao da IA, WhatsApp,
 * e-mail, vendedora).
 *
 * Decisao nao obvia: o centro do anel mostra a QUANTIDADE de vendas, nao o
 * valor -- "R$ 21.543,90" nao cabe legivel num circulo de 128px, e o valor
 * total ja esta na faixa de cima. A lista ao lado traz o valor de cada fatia.
 */

import { ErrorPanel } from '../../../components/PageState';
import { DonutChart, Footnote, SectionCard, type DonutSlice } from '../../../components/ui';
import type { AsyncState } from '../../../lib/api';
import { formatBRLFromCents, formatCount } from '../../../lib/format';
import { CHANNEL_LABEL_FULL, CHANNEL_TONE, type FunnelsSummary } from '../types';

export function ReceitaPorCanal({
  resumo,
  onRetry,
  className = '',
}: {
  readonly resumo: AsyncState<FunnelsSummary>;
  readonly onRetry: () => void;
  readonly className?: string;
}): JSX.Element {
  return (
    <SectionCard id="por-canal" title="Por canal" icon="grafico" description="De onde veio o valor recuperado no período." className={className}>
      <div className="flex h-full flex-col justify-between gap-4">
        {resumo.status === 'loading' ? <div aria-hidden="true" className="h-40 animate-pulse rounded-xl bg-ink/5" /> : null}
        {resumo.status === 'error' ? <ErrorPanel title="Não foi possível carregar a divisão por canal" error={resumo.error} onRetry={onRetry} /> : null}
        {resumo.status === 'success' ? <Anel resumo={resumo.data} /> : null}
        <Footnote>Cada venda é creditada ao último contato antes do pagamento, em qualquer canal.</Footnote>
      </div>
    </SectionCard>
  );
}

function Anel({ resumo }: { readonly resumo: FunnelsSummary }): JSX.Element {
  if (resumo.total.recoveredAmountCents === 0) {
    return <p className="py-10 text-center text-sm text-ink-subtle">Nenhuma venda recuperada no período: o anel fica vazio.</p>;
  }
  const fatias: readonly DonutSlice[] = resumo.channels.map((c) => ({
    key: c.channel,
    label: CHANNEL_LABEL_FULL[c.channel],
    value: c.recoveredAmountCents,
    tone: CHANNEL_TONE[c.channel],
    display: formatBRLFromCents(c.recoveredAmountCents),
  }));
  return (
    <DonutChart
      slices={fatias}
      title="Valor recuperado por canal"
      center={{ value: formatCount(resumo.total.recoveredCount), label: resumo.total.recoveredCount === 1 ? 'venda' : 'vendas' }}
    />
  );
}
